import { ADDITIONAL_PRODUCT } from '@constants';
import {
    AdditionalProductFragment,
    VariantFragment,
} from '@graphql/generated/graphql';
import {
    isVariantDeleted,
    isVariantInStock,
    isVariantOutOfStock,
} from '@utils/variant';
import { useTranslation } from 'next-i18next';
import { useState, useCallback } from 'react';

export type Select = {
    name: string;
    // Marks a select whose option values are variant ids, so the change handler routes
    // it to variant resolution rather than option filtering.
    isFinal?: boolean;
    options: { value: string; disabled?: boolean; label?: string }[];
    value?: string;
};

interface UseSelectsReturn {
    additionalProduct: AdditionalProductFragment | null;
    selectedVariant: VariantFragment | null;
    selects: Select[];
    onSelectOption: (
        optionName: string,
        value?: string,
        isFinal?: boolean
    ) => void;
}

interface Props {
    additionalProduct: AdditionalProductFragment | null;
    defaultSelectedVariant?: VariantFragment | null;
}

export const useVariantSelects = ({
    additionalProduct,
    defaultSelectedVariant,
}: Props): UseSelectsReturn => {
    const { t } = useTranslation(ADDITIONAL_PRODUCT);
    const existingVariants = (additionalProduct?.Variants || []).filter(
        (v) => !isVariantDeleted(v)
    );

    const getOptionMap = useCallback((variants: VariantFragment[]) => {
        const optionMap: Record<string, Set<string>> = {};
        // Build list of options and unique values
        for (const variant of variants) {
            for (const opt of variant.Options ?? []) {
                if (!optionMap[opt.name]) optionMap[opt.name] = new Set();
                optionMap[opt.name].add(opt.value);
            }
        }
        return optionMap;
    }, []);

    const buildSelects = useCallback(
        (
            variants: VariantFragment[],
            selectedVariant?: VariantFragment | null
        ) => {
            // Collect all possible options for each name
            const optionMap = getOptionMap(variants);

            // Build list of selects
            return Object.keys(optionMap).map((name) => ({
                name,
                options: Array.from(optionMap[name]).map((o) => ({
                    value: o,
                    disabled: false,
                })),
                value: selectedVariant?.Options?.find(
                    (opt) => opt.name === name
                )?.value,
            }));
        },
        [getOptionMap]
    );

    const getInitialSelectedVariant = useCallback(() => {
        if (
            defaultSelectedVariant &&
            isVariantInStock(defaultSelectedVariant)
        ) {
            return defaultSelectedVariant;
        }

        if (
            existingVariants.length === 1 &&
            isVariantInStock(existingVariants[0])
        ) {
            return existingVariants[0];
        }

        return null;
    }, [defaultSelectedVariant, existingVariants]);

    const [selectedVariant, setSelectedVariant] =
        useState<VariantFragment | null>(getInitialSelectedVariant());

    const filterVariants = useCallback(
        (variants: VariantFragment[], selects: Select[]) => {
            return variants.filter((variant) => {
                return selects.every((select) => {
                    if (!select.value) return true;
                    return variant.Options?.some(
                        (option) =>
                            option.name === select.name &&
                            option.value === select.value
                    );
                });
            });
        },
        []
    );

    const syncSelectsValueWithVariant = useCallback(
        (selects: Select[], variant: VariantFragment): Select[] => {
            return selects.map((select) => ({
                ...select,
                value: variant.Options?.find((o) => o.name === select.name)
                    ?.value,
            }));
        },
        []
    );

    const updateSelectsDisabled = useCallback(
        (variants: VariantFragment[], selects: Select[]) => {
            return selects.map((select) => {
                return {
                    ...select,
                    options: select.options.map((option) => {
                        const tempSelection = selects.map((s) =>
                            s.name === select.name
                                ? { name: s.name, value: option.value }
                                : s
                        );
                        const matchingVariants = variants.filter((variant) =>
                            tempSelection.every((sel) => {
                                if (!sel.value) return true;
                                return variant.Options?.some(
                                    (opt) =>
                                        opt.name === sel.name &&
                                        opt.value === sel.value
                                );
                            })
                        );
                        const disabled =
                            matchingVariants.length < 1 ||
                            matchingVariants.every(isVariantOutOfStock);
                        return { ...option, disabled };
                    }),
                    value: select.value,
                };
            });
        },
        []
    );

    const [selects, setSelects] = useState<Select[]>(() => {
        const selects = buildSelects(
            existingVariants,
            getInitialSelectedVariant()
        );
        return updateSelectsDisabled(existingVariants, selects);
    });

    const onSelectOption = useCallback(
        (optionName: string, value?: string, isFinal?: boolean) => {
            if (isFinal) {
                setSelectedVariant(
                    existingVariants.find((v) => v.id === value) ?? null
                );
                return;
            }

            let updatedSelects = selects.map((select) =>
                select.name === optionName ? { ...select, value } : select
            );
            let filteredVariants = filterVariants(
                existingVariants,
                updatedSelects
            );

            let newSelectedVariant: VariantFragment | null = null;

            if (
                filteredVariants.length === 1 &&
                filteredVariants[0].id !== selectedVariant?.id
            ) {
                newSelectedVariant = filteredVariants[0];
                updatedSelects = syncSelectsValueWithVariant(
                    selects,
                    newSelectedVariant
                );
                filteredVariants = filterVariants(
                    existingVariants,
                    updatedSelects
                );
            }

            setSelectedVariant(newSelectedVariant);
            setSelects(updateSelectsDisabled(existingVariants, updatedSelects));
        },
        [
            existingVariants,
            selectedVariant,
            selects,
            filterVariants,
            syncSelectsValueWithVariant,
            updateSelectsDisabled,
        ]
    );

    // Tie-breaker: when every real option is chosen yet more than one variant
    // still matches (colliding variants), surface a final synthetic Select that
    // lists the leftover variants so the shopper can pin down exactly one. It is
    // computed inline for rendering only — never written into `variant.Options`
    // and never entering the option map.
    const remainingVariants = filterVariants(existingVariants, selects);
    const allRealSelectsFilled = selects.every((select) => !!select.value);
    const modelSelect: Select | null =
        allRealSelectsFilled && remainingVariants.length > 1
            ? {
                  name: t('variant.model_select_label'),
                  isFinal: true,
                  options: remainingVariants.map((variant) => ({
                      value: variant.id,
                      label: variant.Name,
                      disabled: isVariantOutOfStock(variant),
                  })),
                  value: selectedVariant?.id,
              }
            : null;

    return {
        additionalProduct,
        selectedVariant,
        selects: modelSelect ? [...selects, modelSelect] : selects,
        onSelectOption,
    };
};
